/**
 * MiniProfiler — Lightweight in-game performance profiler.
 *
 * Tracks named timing sections and object counts.
 * Displays a small overlay in the top-right corner of the UIScene.
 * Toggle with F3 key.
 *
 * Usage in GameScene.update():
 *   MiniProfiler.begin('waves');
 *   this.waveManager.update(...);
 *   MiniProfiler.end('waves');
 *
 * Usage for counts:
 *   MiniProfiler.set('robots', count);
 *
 * In UIScene.update():
 *   MiniProfiler.render(this.profilerText);
 */

export class MiniProfiler {
  private static sections: Map<string, number> = new Map();    // accumulated ms per section
  private static starts: Map<string, number> = new Map();      // start timestamp
  private static counters: Map<string, number> = new Map();    // arbitrary counters
  private static history: Map<string, number[]> = new Map();   // rolling averages
  private static visible = false;
  private static frameStart = 0;
  private static frameTotalMs = 0;

  static readonly HISTORY_SIZE = 30; // average over 30 frames (~0.5s)

  /** Call at start of GameScene.update() */
  static frameBegin(): void {
    this.frameStart = performance.now();
    this.sections.clear();
  }

  /** Call at end of GameScene.update() */
  static frameEnd(): void {
    this.frameTotalMs = performance.now() - this.frameStart;
    // Push to history
    for (const [key, val] of this.sections) {
      let h = this.history.get(key);
      if (!h) { h = []; this.history.set(key, h); }
      h.push(val);
      if (h.length > this.HISTORY_SIZE) h.shift();
    }
    let h = this.history.get('_total');
    if (!h) { h = []; this.history.set('_total', h); }
    h.push(this.frameTotalMs);
    if (h.length > this.HISTORY_SIZE) h.shift();
  }

  /** Start timing a named section */
  static begin(name: string): void {
    this.starts.set(name, performance.now());
  }

  /** End timing a named section */
  static end(name: string): void {
    const start = this.starts.get(name);
    if (start === undefined) return;
    const elapsed = performance.now() - start;
    this.sections.set(name, (this.sections.get(name) ?? 0) + elapsed);
  }

  /** Set a counter value (objects, draw calls, etc.) */
  static set(name: string, value: number): void {
    this.counters.set(name, value);
  }

  /** Toggle visibility */
  static toggle(): void {
    this.visible = !this.visible;
  }

  static isVisible(): boolean {
    return this.visible;
  }

  /** Average ms for a section over recent frames */
  private static avg(key: string): number {
    const h = this.history.get(key);
    if (!h || h.length === 0) return 0;
    let sum = 0;
    for (const v of h) sum += v;
    return sum / h.length;
  }

  /** Render profiler text — call from UIScene.update() */
  static render(text: Phaser.GameObjects.Text): void {
    if (!this.visible) {
      text.setVisible(false);
      return;
    }
    text.setVisible(true);

    const lines: string[] = [];
    const totalMs = this.avg('_total');
    lines.push(`UPDATE ${totalMs.toFixed(1)}ms`);
    lines.push('─────────────');

    // Sort sections by avg time descending
    const entries: [string, number][] = [];
    for (const key of this.history.keys()) {
      if (key === '_total') continue;
      entries.push([key, this.avg(key)]);
    }
    entries.sort((a, b) => b[1] - a[1]);

    for (const [name, ms] of entries) {
      const pct = totalMs > 0 ? (ms / totalMs * 100) : 0;
      const bar = '█'.repeat(Math.min(10, Math.round(pct / 10)));
      lines.push(`${name.padEnd(12)} ${ms.toFixed(1).padStart(5)}ms ${pct.toFixed(0).padStart(3)}% ${bar}`);
    }

    // Counters
    if (this.counters.size > 0) {
      lines.push('─────────────');
      for (const [name, val] of this.counters) {
        lines.push(`${name.padEnd(12)} ${String(val).padStart(5)}`);
      }
    }

    // Render budget indicator
    const budget = 16.67; // 60fps target
    const usage = totalMs / budget * 100;
    lines.push('─────────────');
    lines.push(`BUDGET ${usage.toFixed(0)}% of 16.7ms`);

    text.setText(lines.join('\n'));
  }
}
